import React, { useState, useEffect } from 'react';
import { api } from '../App';
import { useAuth } from '../App';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Plus, UserCog, Shield, User, Trash2, Edit, KeyRound } from 'lucide-react';

const ALL_MODULES = [
  { key: 'dashboard', label: 'Dashboard' },
  { key: 'suppliers', label: 'Suppliers' },
  { key: 'customers', label: 'Customers' },
  { key: 'agents', label: 'Sales Agents' },
  { key: 'purchases', label: 'Purchases' },
  { key: 'production', label: 'Production' },
  { key: 'production_costing', label: 'Production Costing' },
  { key: 'orders', label: 'Orders' },
  { key: 'expenses', label: 'Expenses' },
  { key: 'inventory', label: 'Inventory' },
  { key: 'reports', label: 'Reports' },
  { key: 'daily_close', label: 'Daily Close' },
  { key: 'users', label: 'Users' },
  { key: 'audit_log', label: 'Audit Log' },
  { key: 'settings', label: 'Settings' },
];

export default function Users() {
  const { isAdmin, user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'user',
    module_permissions: []
  });

  // Permissions edit dialog (separate)
  const [permsDialogOpen, setPermsDialogOpen] = useState(false);
  const [permsUser, setPermsUser] = useState(null);
  const [permsSelection, setPermsSelection] = useState([]);

  useEffect(() => {
    if (isAdmin) fetchUsers();
  }, [isAdmin]);

  const fetchUsers = async () => {
    try {
      const response = await api.get('/users');
      setUsers(response.data);
    } catch {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.password) {
      toast.error('Please fill all required fields');
      return;
    }
    try {
      // For non-admin role, send module_permissions; admins always get all server-side
      const payload = {
        name: formData.name,
        email: formData.email,
        password: formData.password,
        role: formData.role,
        module_permissions: formData.role === 'admin' ? [] : formData.module_permissions
      };
      await api.post('/auth/register', payload);
      toast.success('User created');
      setDialogOpen(false);
      resetForm();
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    }
  };

  const resetForm = () => {
    setFormData({ name: '', email: '', password: '', role: 'user', module_permissions: [] });
  };

  const handleDeleteUser = async (userId, userName) => {
    if (userId === currentUser?.id) {
      toast.error('You cannot delete your own account');
      return;
    }
    if (!window.confirm(`Delete user "${userName}"? This action cannot be undone.`)) return;
    try {
      await api.delete(`/users/${userId}`);
      toast.success('User deleted');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const openPermsDialog = (user) => {
    setPermsUser(user);
    setPermsSelection(user.module_permissions || []);
    setPermsDialogOpen(true);
  };

  const togglePerm = (key, listSetter, currentList) => {
    if (currentList.includes(key)) {
      listSetter(currentList.filter(k => k !== key));
    } else {
      listSetter([...currentList, key]);
    }
  };

  const savePerms = async () => {
    if (!permsUser) return;
    try {
      await api.put(`/users/${permsUser.id}/permissions`, { module_permissions: permsSelection });
      toast.success('Permissions updated');
      setPermsDialogOpen(false);
      setPermsUser(null);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update permissions');
    }
  };

  const toggleAllPerms = (selectAll, listSetter) => {
    listSetter(selectAll ? ALL_MODULES.map(m => m.key) : []);
  };

  const getRoleBadge = (role) => {
    const variants = {
      admin: { color: 'bg-purple-100 text-purple-700', icon: Shield },
      user: { color: 'bg-blue-100 text-blue-700', icon: User },
      agent: { color: 'bg-green-100 text-green-700', icon: UserCog }
    };
    const config = variants[role] || variants.user;
    const Icon = config.icon;
    return (
      <Badge className={`${config.color} gap-1`}>
        <Icon className="h-3 w-3" />
        {role}
      </Badge>
    );
  };

  if (!isAdmin) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Shield className="h-12 w-12 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-500">Admin access required</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1B4D3E]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="users-page">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-heading font-bold text-[#1B4D3E]">Users</h1>
          <p className="text-slate-500 mt-1">Manage system users & module permissions</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="btn-primary gap-2" data-testid="add-user-btn">
              <Plus className="h-4 w-4" />
              Add User
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading">Create New User</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label>Name *</Label>
                <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="Full name" data-testid="user-name-input" />
              </div>
              <div className="space-y-2">
                <Label>Email *</Label>
                <Input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} placeholder="Email address" data-testid="user-email-input" />
              </div>
              <div className="space-y-2">
                <Label>Password *</Label>
                <Input type="password" value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })} placeholder="Password" data-testid="user-password-input" />
              </div>
              <div className="space-y-2">
                <Label>Role</Label>
                <Select value={formData.role} onValueChange={(v) => setFormData({ ...formData, role: v })}>
                  <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="user">User</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="agent">Agent</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* Module Permissions - only for non-admin */}
              {formData.role !== 'admin' && (
                <div className="space-y-2 pt-2 border-t">
                  <div className="flex items-center justify-between">
                    <Label className="text-base font-semibold">Module Permissions</Label>
                    <div className="flex gap-2">
                      <Button type="button" variant="ghost" size="sm" onClick={() => toggleAllPerms(true, (v) => setFormData({ ...formData, module_permissions: v }))} data-testid="select-all-perms-btn">
                        Select All
                      </Button>
                      <Button type="button" variant="ghost" size="sm" onClick={() => toggleAllPerms(false, (v) => setFormData({ ...formData, module_permissions: v }))}>
                        Clear
                      </Button>
                    </div>
                  </div>
                  <p className="text-xs text-slate-500">Select which modules this user can access. By default, new users have NO access until you check modules below.</p>
                  <div className="grid grid-cols-2 gap-2 bg-slate-50 p-3 rounded-lg">
                    {ALL_MODULES.map(m => (
                      <label key={m.key} className="flex items-center gap-2 cursor-pointer">
                        <Checkbox
                          checked={formData.module_permissions.includes(m.key)}
                          onCheckedChange={() => togglePerm(m.key, (v) => setFormData({ ...formData, module_permissions: v }), formData.module_permissions)}
                          data-testid={`perm-create-${m.key}`}
                        />
                        <span className="text-sm">{m.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
              {formData.role === 'admin' && (
                <div className="bg-purple-50 border border-purple-200 text-purple-700 text-sm p-3 rounded">
                  <Shield className="h-4 w-4 inline mr-1" />
                  Admins automatically have access to all modules.
                </div>
              )}
              
              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }}>Cancel</Button>
                <Button type="submit" className="btn-primary" data-testid="user-submit-btn">Create User</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Users Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg font-heading">All Users</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="table-header">
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Modules</TableHead>
                <TableHead className="text-center">Status</TableHead>
                <TableHead className="text-center">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.length === 0 ? (
                <TableRow><TableCell colSpan={6} className="text-center py-8 text-slate-500">No users found</TableCell></TableRow>
              ) : (
                users.map((u) => (
                  <TableRow key={u.id} className="table-row" data-testid={`user-row-${u.id}`}>
                    <TableCell className="font-medium">{u.name}</TableCell>
                    <TableCell>{u.email}</TableCell>
                    <TableCell>{getRoleBadge(u.role)}</TableCell>
                    <TableCell>
                      {u.role === 'admin' ? (
                        <Badge className="bg-purple-100 text-purple-700">All</Badge>
                      ) : (
                        <span className="text-sm text-slate-600" data-testid={`modules-count-${u.id}`}>
                          {(u.module_permissions || []).length} of {ALL_MODULES.length}
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge className={u.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
                        {u.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-center">
                      <div className="flex items-center justify-center gap-1">
                        {u.role !== 'admin' && (
                          <Button variant="ghost" size="sm" onClick={() => openPermsDialog(u)} className="text-blue-600" title="Edit permissions" data-testid={`edit-perms-${u.id}`}>
                            <KeyRound className="h-4 w-4" />
                          </Button>
                        )}
                        {u.id !== currentUser?.id ? (
                          <Button variant="ghost" size="sm" onClick={() => handleDeleteUser(u.id, u.name)} className="text-red-500 hover:text-red-600" data-testid={`delete-user-btn-${u.id}`} title="Delete user">
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        ) : (
                          <span className="text-slate-400 text-xs px-2">You</span>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Permissions edit dialog */}
      <Dialog open={permsDialogOpen} onOpenChange={(open) => { setPermsDialogOpen(open); if (!open) { setPermsUser(null); setPermsSelection([]); } }}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Module Permissions — {permsUser?.name}</DialogTitle>
          </DialogHeader>
          {permsUser && (
            <div className="space-y-4">
              <p className="text-sm text-slate-500">Select the modules <span className="font-medium">{permsUser.email}</span> can access. Changes apply on their next login.</p>
              <div className="flex gap-2 justify-end">
                <Button type="button" variant="ghost" size="sm" onClick={() => toggleAllPerms(true, setPermsSelection)} data-testid="perms-select-all">Select All</Button>
                <Button type="button" variant="ghost" size="sm" onClick={() => toggleAllPerms(false, setPermsSelection)}>Clear</Button>
              </div>
              <div className="grid grid-cols-2 gap-2 bg-slate-50 p-3 rounded-lg">
                {ALL_MODULES.map(m => (
                  <label key={m.key} className="flex items-center gap-2 cursor-pointer">
                    <Checkbox
                      checked={permsSelection.includes(m.key)}
                      onCheckedChange={() => togglePerm(m.key, setPermsSelection, permsSelection)}
                      data-testid={`perm-edit-${m.key}`}
                    />
                    <span className="text-sm">{m.label}</span>
                  </label>
                ))}
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setPermsDialogOpen(false)}>Cancel</Button>
                <Button onClick={savePerms} className="btn-primary" data-testid="save-perms-btn">Save Permissions</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
